import { applyUiChunk, createUiChunkSessionState } from "./processUiChunkStream";

/**
 * Pure reducer for the `team_child_activity` relay. Folds one relay event into the
 * `Map<runId, entry>` of per-node team member activity and returns the next map — or the SAME
 * reference when the event is a no-op (missing key, finish for an unknown run, or a replay after
 * the run already settled) so the caller can skip a state update.
 *
 * Keyed by runId (one node activation in one user turn) so the same node running again in a later
 * turn gets a fresh card instead of reusing the previous one. `start` opens a card, `chunk` feeds
 * the shared UI-chunk reducer into the run's own message array (so the card streams identically to
 * the agent chat), and `finish` settles the card.
 */
export function reduceTeamActivity(byRun, payload) {
  const { runId, nodeId, agentId, agentName, childSessionId, phase, chunk, label } =
    payload;
  // runId falls back to nodeId for resilience if an older backend omits it.
  const key = runId || nodeId;
  if (!key) return byRun;

  const prev = byRun[key];

  if (phase === "finish") {
    if (!prev) return byRun;
    return {
      ...byRun,
      [key]: { ...prev, status: "done", finishedAt: Date.now() },
    };
  }

  let entry = prev;
  if (!entry) {
    entry = {
      runId: key,
      nodeId: nodeId ?? null,
      agentId: agentId ?? null,
      agentName: agentName ?? null,
      childSessionId: childSessionId ?? null,
      label: label ?? null,
      status: "running",
      cursor: createUiChunkSessionState(childSessionId ?? key),
      messages: [],
      persistedMessages: null,
      startedAt: Date.now(),
      finishedAt: null,
    };
  } else if (entry.status === "done") {
    return byRun; // replay after completion
  }

  if (phase === "start") {
    entry = {
      ...entry,
      nodeId: entry.nodeId ?? nodeId ?? null,
      agentId: entry.agentId ?? agentId ?? null,
      agentName: entry.agentName ?? agentName ?? null,
      childSessionId: entry.childSessionId ?? childSessionId ?? null,
      label: entry.label ?? label ?? null,
    };
  } else if (phase === "chunk" && chunk) {
    const result = applyUiChunk(entry.cursor, chunk, entry.messages);
    entry = { ...entry, cursor: result.cursor, messages: result.messages };
  }

  return { ...byRun, [key]: entry };
}
